import { useEffect, useState } from "react";
import { Box, Flex, Text, Heading, Badge } from "@chakra-ui/react";
import {
  AppButton,
  AppTable,
  TableRow,
  TableCell,
  AppModal,
  AppInput,
  AppSelect,
} from "../../components";
import { colors } from "../../theme";
import { PlusIcon, BlockIcon, EditIcon } from "../../utils/svg";
import { Controller, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import {
  useMallAdmins,
  useCreateMallAdmin,
  useMalls,
  useActivateUser,
  useBlockUser,
} from "../../services/useQueries";
import { useDispatch } from "react-redux";
import { setLoading, showToast } from "../../redux/reducer/utilsSlice";

/**
 * Super-Admin-only screen (GAP-018): provision and manage MALL_ADMIN
 * accounts. Each mall admin is bound to exactly one mall and the backend
 * scopes all their admin-panel data to it.
 */
const MallAdminManagementScreen = () => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  // Separate form for the block/activate reason modal. Sharing one form with
  // the create modal meant the create modal's required fields stayed
  // registered and could fail validation on the reason submit (silently
  // no-oping block/activate). A dedicated form avoids that entirely.
  const {
    register: registerReason,
    handleSubmit: handleSubmitReason,
    reset: resetReason,
    formState: { errors: reasonErrors },
  } = useForm();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [actionType, setActionType] = useState(null); // 'block' | 'activate'

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { data: mallAdminsRes, isLoading: mallAdminsLoading } = useMallAdmins();
  const mallAdmins = mallAdminsRes?.data || [];
  const { data: malls = [] } = useMalls();
  const createMallAdminMutation = useCreateMallAdmin();
  const activateUserMutation = useActivateUser();
  const blockUserMutation = useBlockUser();

  useEffect(() => {
    dispatch(setLoading(mallAdminsLoading));
  }, [mallAdminsLoading]);

  const headers = ["Name", "Email", "Phone", "Mall", "Status", "Actions"];

  const handleAddMallAdmin = () => {
    reset({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      phoneNumber: "",
      mallId: "",
    });
    setIsModalOpen(true);
  };

  const onSubmit = (data) => {
    createMallAdminMutation.mutate(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        mallId: data.mallId,
        ...(data.phoneNumber ? { phoneNumber: data.phoneNumber } : {}),
      },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          dispatch(showToast("Mall admin created"));
        },
      }
    );
  };

  const handleStatusAction = (admin, type) => {
    setSelectedAdmin(admin);
    setActionType(type);
    resetReason({ reason: "" });
    setIsReasonModalOpen(true);
  };

  const handleReasonSubmit = (data) => {
    if (!selectedAdmin) return;
    const payload = { reason: data.reason };
    const mutation =
      actionType === "block" ? blockUserMutation : activateUserMutation;
    mutation.mutate(
      { id: selectedAdmin.id, data: payload },
      {
        onSuccess: () => {
          setIsReasonModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ["mallAdmins"] });
        },
      }
    );
  };

  const renderRow = (admin, index) => {
    const isActive = admin.Status !== "BLOCKED";
    const fullName =
      [admin.firstName, admin.lastName].filter(Boolean).join(" ") ||
      admin.name ||
      "-";

    return (
      <TableRow key={admin.id || index}>
        <TableCell fontWeight="500">{fullName}</TableCell>
        <TableCell>{admin.email}</TableCell>
        <TableCell>{admin.phoneNumber || "-"}</TableCell>
        <TableCell>{admin.managedMall?.name || "-"}</TableCell>
        <TableCell>
          <Badge
            colorPalette={isActive ? "green" : "red"}
            px={2}
            py={1}
            borderRadius="full"
            fontSize="xs"
            textTransform="capitalize"
          >
            {isActive ? "Active" : "Blocked"}
          </Badge>
        </TableCell>
        <TableCell>
          <Flex gap={2}>
            {isActive ? (
              <AppButton
                variant="ghost"
                size="sm"
                onClick={() => handleStatusAction(admin, "block")}
                leftIcon={<BlockIcon boxSize={4} color="#EF4444" />}
              />
            ) : (
              <AppButton
                variant="ghost"
                size="sm"
                onClick={() => handleStatusAction(admin, "activate")}
                leftIcon={<EditIcon boxSize={4} color="#22C55E" />}
              />
            )}
          </Flex>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={colors.text} mb={1}>
            Mall Admins
          </Heading>
          <Text color={colors.textSecondary}>
            Provision mall-scoped administrator accounts
          </Text>
        </Box>
        <AppButton
          variant="primary"
          leftIcon={<PlusIcon boxSize={5} />}
          onClick={handleAddMallAdmin}
        >
          Add Mall Admin
        </AppButton>
      </Flex>

      {/* Table */}
      <AppTable
        headers={headers}
        data={mallAdmins}
        renderRow={renderRow}
        isLoading={mallAdminsLoading}
      />

      {/* Add Mall Admin Modal */}
      <AppModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Add Mall Admin"
        size="lg"
        footer={
          <Flex gap={3} justify="flex-end" w="100%">
            <AppButton variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </AppButton>
            <AppButton
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              isLoading={createMallAdminMutation.isPending}
            >
              Create Mall Admin
            </AppButton>
          </Flex>
        }
      >
        <Flex direction="column" gap={4}>
          <Flex gap={4}>
            <Box flex={1}>
              <AppInput
                label="First Name"
                name="firstName"
                placeholder="Enter first name"
                register={register}
                errors={errors}
                required
              />
            </Box>
            <Box flex={1}>
              <AppInput
                label="Last Name"
                name="lastName"
                placeholder="Enter last name"
                register={register}
                errors={errors}
                required
              />
            </Box>
          </Flex>
          <AppInput
            label="Email Address"
            name="email"
            type="email"
            placeholder="Enter email"
            register={register}
            errors={errors}
            required
          />
          <AppInput
            label="Password"
            name="password"
            type="password"
            placeholder="Minimum 8 characters"
            register={register}
            errors={errors}
            required
          />
          <AppInput
            label="Phone Number (optional)"
            name="phoneNumber"
            placeholder="Enter phone number"
            register={register}
            errors={errors}
          />
          <Controller
            control={control}
            name="mallId"
            rules={{ required: "Mall is required" }}
            render={({ field }) => (
              <AppSelect
                label="Managed Mall"
                placeholder="Select mall"
                options={malls.map((m) => ({ value: m.id, label: m.name }))}
                value={field.value}
                onChange={field.onChange}
                error={errors.mallId}
              />
            )}
          />
        </Flex>
      </AppModal>

      {/* Block/Activate Reason Modal */}
      <AppModal
        isOpen={isReasonModalOpen}
        onClose={() => setIsReasonModalOpen(false)}
        title={actionType === "block" ? "Block Mall Admin" : "Activate Mall Admin"}
        size="md"
        footer={
          <Flex gap={3} justify="flex-end" w="100%">
            <AppButton
              variant="secondary"
              onClick={() => setIsReasonModalOpen(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              variant="primary"
              onClick={handleSubmitReason(handleReasonSubmit)}
              isLoading={
                blockUserMutation.isPending || activateUserMutation.isPending
              }
            >
              {actionType === "block" ? "Block" : "Activate"}
            </AppButton>
          </Flex>
        }
      >
        <Flex direction="column" gap={4}>
          <AppInput
            label="Reason"
            name="reason"
            placeholder="Enter reason for this action"
            register={registerReason}
            errors={reasonErrors}
            required
          />
        </Flex>
      </AppModal>
    </Box>
  );
};

export default MallAdminManagementScreen;
